import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import React from 'react';
import { useTranslation } from 'react-i18next';
import useClockTime from '../../../../Hooks/useClockTime';
import { ComplianceChecklistItemStatus } from '../../../../generated/core-api/graphql-react-query';
import { SessionComplianceChecklistItem } from '../../../LiveContext/NewSessionContext/Contexts/SessionComplianceContextProvider/type';
import complianceStatusTag from '../complianceStatusTag';
import ComplianceItemContent from '../ComplianceItemContent';
import TopicTag from '../TopicTag';
import StyledBox from './styles';

interface ComplianceProcessedGroupProps {
  items: SessionComplianceChecklistItem[];
  density: 'panel' | 'page';
  defaultOpen?: boolean;
}

function ResolvedAt(props: { item: SessionComplianceChecklistItem }) {
  const { item } = props;
  const { t } = useTranslation();
  const time = useClockTime(item.requiredAt ?? 0);
  return (
    <>{t(`right-panel.session-compliance.card.resolvedAt.${item.result}`, { time })}</>
  );
}

function ComplianceProcessedRow(props: { item: SessionComplianceChecklistItem, density: 'panel' | 'page' }) {
  const { item, density } = props;
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  return (
    <div className="complianceGroupRow">
      <button type="button" className="complianceGroupRowHeader" onClick={() => setOpen((current) => !current)}>
        <span className="complianceGroupRowHeaderLeft">
          <span className="complianceGroupRowTitle"><TopicTag text={item.topic.text} color={item.topic.color} density={density} /></span>
          {density === 'page' && <span className="complianceGroupRowTimestamp"><ResolvedAt item={item} /></span>}
        </span>
        <span className="complianceGroupRowHeaderRight">
          {complianceStatusTag(item.result, t)}
          {density === 'page' ? (
            <span className="complianceGroupToggle">
              {open ? t('right-panel.session-compliance.card.collapse') : t('right-panel.session-compliance.card.expand')}
            </span>
          ) : <ExpandMoreRounded style={{ transform: open ? 'rotate(180deg)' : undefined, fontSize: 18 }} />}
        </span>
      </button>
      {open && (
        <div className="complianceGroupRowBody">
          <ComplianceItemContent item={item} density={density} />
          {density === 'panel' && (
            <span className="complianceGroupRowResolvedAt"><ResolvedAt item={item} /></span>
          )}
        </div>
      )}
    </div>
  );
}

// The "Traités" section: validated/rejected items collapsed into one bordered
// box, itself collapsible (closed by default in the panel, open by default on
// the full page), with each row independently expandable to the same content
// a pending item's card shows (ComplianceItemContent).
export default function ComplianceProcessedGroup(props: ComplianceProcessedGroupProps) {
  const { items, density, defaultOpen = false } = props;
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(defaultOpen);

  const validatedCount = items.filter((item) => item.result === ComplianceChecklistItemStatus.Validated).length;
  const rejectedCount = items.filter((item) => item.result === ComplianceChecklistItemStatus.Rejected).length;
  const summary = [
    ...(validatedCount ? [t('home-page.compliance.mandatory.commands-passed.validated', { count: validatedCount })] : []),
    ...(rejectedCount ? [t('home-page.compliance.mandatory.commands-passed.rejected', { count: rejectedCount })] : []),
  ].join(' · ');

  return (
    <StyledBox density={density}>
      <button type="button" className="complianceGroupHeader" onClick={() => setOpen((current) => !current)}>
        {density === 'page' ? (
          <span>{`${t('right-panel.session-compliance.card.processedTitle')} — ${summary}`}</span>
        ) : (
          <span className="complianceGroupHeaderLabels">
            <span className="complianceGroupTitle">{t('right-panel.session-compliance.card.processedTitle')}</span>
            <span className="complianceGroupSummary">{summary}</span>
          </span>
        )}
        {density === 'page' ? (
          <span className="complianceGroupToggle">
            {open ? t('right-panel.session-compliance.card.collapse') : t('right-panel.session-compliance.card.expand')}
          </span>
        ) : <ExpandMoreRounded style={{ transform: open ? 'rotate(180deg)' : undefined }} />}
      </button>
      {open && (
        <div className="complianceGroupItems">
          {items.map((item) => (
            <ComplianceProcessedRow key={item.textLower} item={item} density={density} />
          ))}
        </div>
      )}
    </StyledBox>
  );
}
